import {
  type ErrorLogEntry,
  type ErrorLogContext,
  type ErrorLogRequest,
  type ErrorMessageLevel,
  errorLogEntrySchema,
  errorLogRequestSchema,
} from '@agm/shared-schemas';

class ErrorLogger {
  private queue: ErrorLogEntry[] = [];
  private isOnline = navigator.onLine;

  constructor() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    window.addEventListener('error', this.handleGlobalError);
    window.addEventListener(
      'unhandledrejection',
      this.handleUnhandledRejection,
    );
  }

  private handleGlobalError = (event: ErrorEvent) => {
    this.logError({
      message: event.message,
      stack: event.error?.stack,
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    this.logError({
      message: `Unhandled Promise Rejection: ${event.reason}`,
      stack: event.reason?.stack,
      extra: {
        type: 'unhandledRejection',
        reason: event.reason,
      },
    });
  };

  logError(error: ErrorLogRequest) {
    const validatedError = errorLogRequestSchema.parse(error);
    const entry: ErrorLogEntry = {
      message: validatedError.message,
      stack: validatedError.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      extra: validatedError.extra,
    };

    const validatedEntry = errorLogEntrySchema.parse(entry);

    if (import.meta.env.DEV) {
      console.error('Error logged:', entry);
    }

    if (this.isOnline) {
      this.sendToService(validatedEntry);
    } else {
      this.queue.push(validatedEntry);
      this.saveToLocalStorage();
    }
  }

  private async sendToService(entry: ErrorLogEntry) {
    try {
      if (import.meta.env.PROD) {
        console.log('Would send to error service:', entry);

        // Example integration points:
        // await fetch('/api/errors', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify(entry),
        // });

        // Or use a third-party service:
        // if (window.Sentry) {
        //   window.Sentry.captureException(new Error(entry.message), {
        //     extra: entry.extra,
        //   });
        // }
      }
    } catch (error) {
      console.error('Failed to send error to service:', error);
      this.queue.push(entry);
      this.saveToLocalStorage();
    }
  }

  private saveToLocalStorage() {
    try {
      const existing = localStorage.getItem('error_queue');
      const existingQueue = existing ? JSON.parse(existing) : [];
      const combined = [...existingQueue, ...this.queue].slice(-50); // Keep last 50 errors
      localStorage.setItem('error_queue', JSON.stringify(combined));
    } catch (error) {
      console.error('Failed to save errors to localStorage:', error);
    }
  }

  private loadFromLocalStorage(): ErrorLogEntry[] {
    try {
      const stored = localStorage.getItem('error_queue');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private async flushQueue() {
    const storedErrors = this.loadFromLocalStorage();
    const allErrors = [...storedErrors, ...this.queue];

    this.queue = [];
    localStorage.removeItem('error_queue');

    for (const entry of allErrors) {
      await this.sendToService(entry);
    }
  }

  captureException(error: Error, extra?: ErrorLogContext) {
    this.logError({
      message: error.message,
      stack: error.stack,
      extra: {
        ...extra,
        name: error.name,
      },
    });
  }

  captureMessage(
    message: string,
    level: ErrorMessageLevel = 'error',
    extra?: ErrorLogContext,
  ) {
    if (level === 'error') {
      this.logError({
        message,
        extra: {
          ...extra,
          level,
        },
      });
    } else if (import.meta.env.DEV) {
      if (level === 'warning') {
        console.warn(message, extra);
      } else {
        console.info(message, extra);
      }
    }
  }
}

export const errorLogger = new ErrorLogger();
